import {
  assignTask,
  cancelTaskExecution,
  cancelTaskRequest,
  closeTaskRequest,
  createTaskRequest,
  fetchEligibleAssignees,
  fetchTaskRequestDetail,
  fetchTaskRequests,
  reassignTask,
} from '@/features/task-assignment/api/taskAssignments';
import {
  TASK_ASSIGNMENT_KEY,
  TASK_REQUEST_PAGE_SIZE,
} from '@/features/task-assignment/config/taskAssignment';
import type {
  AssignTaskInput,
  CancelTaskExecutionInput,
  CancelTaskRequestInput,
  CloseTaskRequestInput,
  CreateTaskRequestInput,
  ReassignTaskInput,
  TaskRequestFilters,
} from '@/features/task-assignment/types/taskAssignment';
import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';

const taskAssignmentKeys = {
  root: [TASK_ASSIGNMENT_KEY] as const,
  requests: () => [TASK_ASSIGNMENT_KEY, 'requests'] as const,
  requestList: (filters: TaskRequestFilters) => [TASK_ASSIGNMENT_KEY, 'requests', filters] as const,
  request: (requestId: string) => [TASK_ASSIGNMENT_KEY, 'request', requestId] as const,
  eligibleRoot: (requestId: string) => [TASK_ASSIGNMENT_KEY, 'eligible', requestId] as const,
  eligible: (requestId: string, query?: string) =>
    [...taskAssignmentKeys.eligibleRoot(requestId), query ?? ''] as const,
};

export function taskRequestsQuery(filters: TaskRequestFilters, enabled = true) {
  return infiniteQueryOptions({
    queryKey: taskAssignmentKeys.requestList(filters),
    queryFn: ({ pageParam, signal }) =>
      fetchTaskRequests(filters, pageParam, TASK_REQUEST_PAGE_SIZE, { signal }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    placeholderData: keepPreviousData,
    enabled,
    throwOnError: false,
  });
}

export function useTaskRequests(filters: TaskRequestFilters, enabled = true) {
  return useInfiniteQuery(taskRequestsQuery(filters, enabled));
}

export function taskRequestDetailQuery(requestId: string) {
  return queryOptions({
    queryKey: taskAssignmentKeys.request(requestId),
    queryFn: ({ signal }) => fetchTaskRequestDetail(requestId, { signal }),
  });
}

export function useTaskRequestDetailSuspense(requestId: string) {
  return useSuspenseQuery(taskRequestDetailQuery(requestId));
}

function eligibleAssigneesQuery(requestId: string, query?: string, enabled = true) {
  return queryOptions({
    queryKey: taskAssignmentKeys.eligible(requestId, query),
    queryFn: ({ signal }) => fetchEligibleAssignees(requestId, query, { signal }),
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useEligibleAssignees(requestId: string, query?: string, enabled = true) {
  return useQuery(eligibleAssigneesQuery(requestId, query, enabled));
}

export function useCreateTaskRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTaskRequestInput) => createTaskRequest(input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: taskAssignmentKeys.requests() }),
  });
}

export function useAssignTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: AssignTaskInput) => assignTask(input.requestId, input.assigneeId),
    onSuccess: (_, input) =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: taskAssignmentKeys.request(input.requestId) }),
        queryClient.invalidateQueries({ queryKey: taskAssignmentKeys.requests() }),
        queryClient.invalidateQueries({
          queryKey: taskAssignmentKeys.eligibleRoot(input.requestId),
        }),
      ]),
  });
}

export function useReassignTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ReassignTaskInput) => reassignTask(input.executionId, input.assigneeId),
    onSuccess: (_, input) =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: taskAssignmentKeys.request(input.requestId) }),
        queryClient.invalidateQueries({ queryKey: taskAssignmentKeys.requests() }),
        queryClient.invalidateQueries({
          queryKey: taskAssignmentKeys.eligibleRoot(input.requestId),
        }),
      ]),
  });
}

export function useCancelTaskExecution() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CancelTaskExecutionInput) =>
      cancelTaskExecution(input.executionId, input.reason),
    onSuccess: (_, input) =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: taskAssignmentKeys.request(input.requestId) }),
        queryClient.invalidateQueries({ queryKey: taskAssignmentKeys.requests() }),
        queryClient.invalidateQueries({
          queryKey: taskAssignmentKeys.eligibleRoot(input.requestId),
        }),
      ]),
  });
}

export function useCloseTaskRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CloseTaskRequestInput) => closeTaskRequest(input.requestId),
    onSuccess: (_, input) =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: taskAssignmentKeys.request(input.requestId) }),
        queryClient.invalidateQueries({ queryKey: taskAssignmentKeys.requests() }),
        queryClient.invalidateQueries({
          queryKey: taskAssignmentKeys.eligibleRoot(input.requestId),
        }),
      ]),
  });
}

export function useCancelTaskRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CancelTaskRequestInput) => cancelTaskRequest(input.requestId, input.reason),
    onSuccess: (_, input) =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: taskAssignmentKeys.request(input.requestId) }),
        queryClient.invalidateQueries({ queryKey: taskAssignmentKeys.requests() }),
        queryClient.invalidateQueries({
          queryKey: taskAssignmentKeys.eligibleRoot(input.requestId),
        }),
      ]),
  });
}
